import {
  resolveIndexCollectionHeading,
  useIndexSection,
} from '@/components/page-sections/index-section';
import { IndexCollectionBlocks } from '@/components/page-sections/index-section-collection-blocks';
import {
  readConstrainedIndexLayoutProps,
  readIndexSectionViewVariant,
} from '@/components/page-sections/index-section-utils';
import { IndexSectionView } from '@/components/page-sections/index-section-views';
import {
  usePageSectionContext,
  usePageSectionData,
} from '@/components/page-sections/page-section-context';
import { EmptyState } from '@/components/shared/empty-state';
import { useIsPreviewMode } from '@/hooks/use-is-preview-mode';
import { isIndexCollectionBlock } from '@/lib/blocks/collection/index-collection/blocks';
import { caseResultToCollectionItem } from '@/lib/blocks/collection/item-mappers/case-result';
import type { CaseResultRecord } from '@/lib/blocks/collection/records';
import { readRecord, readString } from '@/lib/prop-utils';
import type { PageBlock } from '@/types/block';
import { type CaseResultIndexProps, CaseResultIndexVariant } from './definition';
import { readCaseResultIndexProps } from './display-options';
import { normalizeCaseResult, resolveCaseResultFilterPracticeAreas } from './normalizers';
import { CaseResultPinnedChrome } from './pinned-chrome';
import { TEMPLATE_PREVIEW_CASE_RESULTS } from './preview-data';

const CASE_RESULT_INDEX_COLLECTION_VARIANTS = {
  list: CaseResultIndexVariant.LIST_LAYOUT,
  grid: CaseResultIndexVariant.GRID_LAYOUT,
  split: CaseResultIndexVariant.SPLIT_LAYOUT,
};
const CASE_RESULT_COLLECTION_HEADING = 'All Case Results';

function hasLiveCaseResultIdentity(caseResult: CaseResultRecord | null | undefined) {
  return Boolean(caseResult?.pageId);
}

function readPinnedCollectionBackgroundColor(blocks: PageBlock[], variant: string) {
  const block = blocks.find(isIndexCollectionBlock);
  const indexVariantProps = readRecord(block?.props?.indexVariantProps);
  const activeVariantProps = readRecord(indexVariantProps?.[variant]);

  return readString(activeVariantProps?.backgroundColor);
}

export function CaseResultIndexSection() {
  const { props, variant = CaseResultIndexVariant.GRID_LAYOUT } =
    usePageSectionData<CaseResultIndexProps>();
  const sectionContext = usePageSectionContext();
  const isPreview = useIsPreviewMode();
  const isStaticPreview = sectionContext ? sectionContext.renderMode !== 'default' : false;
  const isLayoutPreview = sectionContext?.renderMode === 'layout_preview';
  const sectionProps = readCaseResultIndexProps(props);
  const collectionHeading = resolveIndexCollectionHeading(CASE_RESULT_COLLECTION_HEADING);
  const sectionCaseResults = sectionProps.caseResults?.data ?? [];
  const controllerInput = {
    filterOrdering: sectionProps.filterOrdering,
    backgroundProps: sectionProps.backgroundProps,
    pagination: sectionProps.caseResults?.pagination,
  };
  const hasResolvedCaseResults = isLayoutPreview
    ? false
    : isStaticPreview
      ? sectionCaseResults.some(hasLiveCaseResultIdentity)
      : sectionCaseResults.length > 0;
  const usesPreviewCaseResults = isStaticPreview && !hasResolvedCaseResults;
  const resolvedPinnedCaseResults = isLayoutPreview
    ? [TEMPLATE_PREVIEW_CASE_RESULTS[0]].map(normalizeCaseResult)
    : Array.isArray(sectionProps.caseResults?.pinned)
      ? sectionProps.caseResults.pinned.map(normalizeCaseResult)
      : Array.isArray(sectionProps.pinned)
        ? sectionProps.pinned.map(normalizeCaseResult)
        : [];
  const caseResultsList =
    hasResolvedCaseResults || !isStaticPreview
      ? sectionCaseResults
      : TEMPLATE_PREVIEW_CASE_RESULTS.slice(1);
  const normalizedCaseResults = caseResultsList.map(normalizeCaseResult);
  const caseResultsForFilters = [...resolvedPinnedCaseResults, ...normalizedCaseResults];
  const practiceAreas = resolveCaseResultFilterPracticeAreas({
    sectionPracticeAreas: sectionProps.practiceAreas,
    caseResults: caseResultsForFilters,
    isTemplatePreview: usesPreviewCaseResults,
  });

  const {
    sortedFilters: sortedPracticeAreas,
    hasRecords: hasCaseResults,
    shouldShowPinned,
    pinnedRecords,
    visibleRecords: visibleCaseResults,
    hasVisibleRecords: hasVisibleCaseResults,
    sectionBackgroundColor,
    pinnedCollectionBlocks,
    listCollectionBlocks,
    filterBlock,
    hasPinnedCollectionBlocks,
    hasListCollectionBlocks,
    paginationNode,
  } = useIndexSection({
    ...controllerInput,
    records: normalizedCaseResults,
    pinnedRecords: resolvedPinnedCaseResults,
    filters: practiceAreas,
  });
  const layoutProps = readConstrainedIndexLayoutProps(sectionProps.backgroundProps);

  if (!hasCaseResults && !shouldShowPinned) {
    return (
      <EmptyState message="No case results available. Add case results to display them here." />
    );
  }

  const renderCollectionBlocks = (
    caseResults: CaseResultRecord[],
    blocks: typeof pinnedCollectionBlocks,
  ) => (
    <IndexCollectionBlocks
      blocks={blocks}
      records={caseResults}
      sectionId={sectionContext?.sectionId}
      editorSectionId={sectionContext?.editorSectionUiKey}
      isStaticPreview={isStaticPreview}
      isPreview={isPreview}
      variant={variant}
      contentType="case_result"
      variants={CASE_RESULT_INDEX_COLLECTION_VARIANTS}
      toCollectionItem={caseResultToCollectionItem}
    />
  );
  const renderedPinnedCollectionBlocks = renderCollectionBlocks(
    pinnedRecords,
    pinnedCollectionBlocks,
  );
  const pinnedBackgroundColor =
    readString(sectionProps.backgroundProps.backgroundColor) ??
    readPinnedCollectionBackgroundColor(pinnedCollectionBlocks, variant);
  const pinnedCollectionBody = (
    <CaseResultPinnedChrome
      variant={variant}
      layoutProps={layoutProps}
      backgroundProps={sectionProps.backgroundProps}
      backgroundColor={pinnedBackgroundColor}
      pinnedHeading={sectionProps.pinnedHeading}
      pinnedDescription={sectionProps.pinnedDescription}
    >
      {renderedPinnedCollectionBlocks}
    </CaseResultPinnedChrome>
  );

  return (
    <IndexSectionView
      variant={readIndexSectionViewVariant(variant)}
      layoutProps={layoutProps}
      sectionBackgroundColor={sectionBackgroundColor}
      filters={{
        show: sectionProps.showFilters,
        limit: sectionProps.filterLimit,
        items: sortedPracticeAreas,
        key: 'practice_areas',
        label: 'Practice Areas',
        block: filterBlock,
      }}
      pinnedCollection={{
        show: shouldShowPinned,
        hasCollectionBlocks: hasPinnedCollectionBlocks,
        fullWidth: variant !== CaseResultIndexVariant.SPLIT_LAYOUT,
        body: pinnedCollectionBody,
      }}
      collection={{
        hasVisibleRecords: hasVisibleCaseResults,
        hasCollectionBlocks: hasListCollectionBlocks,
        heading: collectionHeading,
        body: renderCollectionBlocks(visibleCaseResults, listCollectionBlocks),
        pagination: paginationNode,
      }}
    />
  );
}
